import { Injectable } from '@angular/core';
import { WijmoAutoSaveService } from './wijmo-auto-save.service';
import { appState } from '../store/appstate';

@Injectable({
  providedIn: 'root'
})
export class StorageService {

  constructor( private _WijmoAutoSaveService: WijmoAutoSaveService) { }
  setLocalStorage(key, value) {
    localStorage.setItem(key, JSON.stringify(value));
  }
  getLocalStorage(key) {
        return localStorage.getItem(key);
  }
  removedLocalStorage(key) {
    localStorage.removeItem(key);
  }
  setSessionStorage(key, value) {
    sessionStorage.setItem(key, JSON.stringify(value));
  }
  getSessionStorage(key) {
    return sessionStorage.getItem(key);
  }
  removedSessionStorage(key) {
    sessionStorage.removeItem(key);
  }
  storeWijmoGridDataInSession(flex, count?, wijmokey?) {
    const gridData = flex?.columns?.map(column => ({
      header: column.header,
      width: column.width,
      isVisible: column.isVisible,
      minWidth: column.minWidth
    }));
    if (wijmokey) {
      const storeKey = `applyDefault_${wijmokey}`;
      appState[storeKey] = "false";
    }
    this.setSessionStorage(`flexGridData_${count ? count : "0"}`, gridData);
    this.setSessionStorage(`clientWidth_${count ? count : "0"}`, flex["_e"].clientWidth);
  }
  getWijmoGridDataInSession(wjHeaders, key, count?, applyNewChecks?: boolean) {
    let wijmoSavedWidths = this.getSessionStorage(`flexGridData_${count ? count : "0"}`);
    let clientWidth = this.getSessionStorage(`clientWidth_${count ? count : "0"}`);
    const storeKey = `applyDefault_${key}`;
    if (!applyNewChecks || (applyNewChecks && appState[storeKey] === "false")) {
      if (wijmoSavedWidths) {
          this._WijmoAutoSaveService.storeWijimoStatus( JSON.parse(wijmoSavedWidths) ,key, wjHeaders, clientWidth);
      }
      appState[storeKey] = "";
    }
    if (wijmoSavedWidths) {
      this.removedSessionStorage(`flexGridData_${count ? count : "0"}`);
      this.removedSessionStorage(`clientWidth_${count ? count : "0"}`);
    }
  }
  getSafeLocalStorage(key: string, storage: Storage = localStorage): string | null {
    try {
      if (typeof window !== 'undefined' && storage) {
        return storage.getItem(key);
      }
    } catch (err) {
      console.warn(`Storage getItem failed for key "${key}":`, err);
    }
    return null;
  }

  setSafeLocalStorage(key: string, value: string, storage: Storage = localStorage): boolean {
    try {
      if (typeof window !== 'undefined' && storage) {
        storage.setItem(key, value);
        return true;
      }
    } catch (err) {
      console.warn(`Storage setItem failed for key "${key}":`, err);
    }
    return false;
  }

  removeSafeLocalStorage(key: string, storage: Storage = localStorage): void {
    try {
      if (typeof window !== 'undefined' && storage) {
        storage.removeItem(key);
      }
    } catch (err) {
      console.warn(`Storage removeItem failed for key "${key}":`, err);
    }
  }

  /**
   * Encrypts data using base64 encoding
   * @param value - The string value to encrypt
   * @returns Encrypted base64 string
   */
  encryptData(value: string): string {
    try {
      return btoa(value);
    } catch (error) {
      console.error('Error encrypting data:', error);
      return value;
    }
  }

  /**
   * Decrypts base64 encoded data
   * @param value - The base64 encoded string to decrypt
   * @returns Decrypted string
   */
  decryptData(value: string): string {
    try {
      return atob(value);
    } catch (error) {
      console.error('Error decrypting data:', error);
      return value;
    }
  }

  /**
   * Securely stores data with encryption
   * @param key - Storage key
   * @param value - Value to store (will be stringified and encrypted)
   */
  set(key: string, value: any): void {
    try {
      const stringValue = typeof value === 'string' ? value : JSON.stringify(value);
      const encryptedValue = this.encryptData(stringValue);
      localStorage.setItem(key, encryptedValue);
    } catch (error) {
      console.error(`Error setting secure storage for key "${key}":`, error);
    }
  }

  /**
   * Retrieves and decrypts stored data
   * @param key - Storage key
   * @returns Decrypted and parsed value or null
   */
  get(key: string): any {
    try {
      const encryptedValue = localStorage.getItem(key);
      if (!encryptedValue) {
        return null;
      }
      const decryptedValue = this.decryptData(encryptedValue);
      try {
        return JSON.parse(decryptedValue);
      } catch {
        return decryptedValue;
      }
    } catch (error) {
      console.error(`Error getting secure storage for key "${key}":`, error);
      return null;
    }
  }

  /**
   * Removes securely stored data
   * @param key - Storage key
   */
  remove(key: string): void {
    try {
      localStorage.removeItem(key);
    } catch (error) {
      console.error(`Error removing secure storage for key "${key}":`, error);
    }
  }
}
